import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LINKS = [
  { label: "Platform", to: "/#platform", testid: "nav-link-platform" },
  { label: "For Media", to: "/for-media", testid: "nav-link-media" },
  { label: "For Journalists", to: "/for-journalists", testid: "nav-link-journalists" },
  { label: "Methodology", to: "/methodology", testid: "nav-link-methodology" },
  { label: "About", to: "/about", testid: "nav-link-about" },
  { label: "Contact", to: "/contact", testid: "nav-link-contact" },
];

const NavItem = ({ link, onClick }) => {
  const location = useLocation();
  const isHashLink = link.to.includes("#");
  const base = "text-sm font-medium transition-colors duration-200";
  if (isHashLink) {
    const active = location.pathname === "/" && location.hash === "#platform";
    return (
      <Link
        to={link.to}
        data-testid={link.testid}
        onClick={onClick}
        className={`${base} ${active ? "text-[#b91c1c]" : "text-slate-600 hover:text-slate-900"}`}
      >
        {link.label}
      </Link>
    );
  }
  return (
    <NavLink
      to={link.to}
      data-testid={link.testid}
      onClick={onClick}
      className={({ isActive }) => `${base} ${isActive ? "text-[#b91c1c]" : "text-slate-600 hover:text-slate-900"}`}
    >
      {link.label}
    </NavLink>
  );
};

const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/85 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo" onClick={() => setOpen(false)}>
          <img src="/anlytx-logo.png" alt="Anlytx logo" className="h-9 w-9 rounded-md" />
          <span className="leading-tight">
            <span className="block font-heading text-base font-bold tracking-tight text-slate-900">
              Anlytx for Elections
            </span>
            <span className="block font-mono-data text-[10px] uppercase tracking-[0.18em] text-slate-400">
              A SevenM Product
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {LINKS.map((link) => (
            <NavItem key={link.testid} link={link} />
          ))}
          <Link
            to="/contact#enquiry-form"
            data-testid="nav-request-access-btn"
            className="rounded-md bg-[#b91c1c] px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#991b1b]"
          >
            Request Access
          </Link>
        </nav>

        <button
          type="button"
          data-testid="nav-menu-button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-700 lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-slate-200 bg-white lg:hidden"
            aria-label="Mobile"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((link) => (
                <div key={`m-${link.testid}`} className="border-b border-slate-100 py-3 last:border-0">
                  <NavItem link={link} onClick={() => setOpen(false)} />
                </div>
              ))}
              <Link
                to="/contact#enquiry-form"
                data-testid="mobile-request-access-btn"
                onClick={() => setOpen(false)}
                className="mt-2 rounded-md bg-[#b91c1c] px-5 py-3 text-center text-sm font-semibold text-white"
              >
                Request Access
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
